/** Marketing Overview — funnel dashboard: time-series + top campaigns/sequences. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Send, GitBranch, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const RANGES = [7, 30, 90];
const SERIES_KEYS = [
  { key: "sent", color: "#6366f1" },
  { key: "delivered", color: "#0ea5e9" },
  { key: "opened", color: "#10b981" },
  { key: "clicked", color: "#f59e0b" },
  { key: "replied", color: "#ec4899" },
];
const FUNNEL_STAGES = ["sent", "delivered", "opened", "clicked", "replied"];
const STAGE_COLORS = { sent: "bg-indigo-500", delivered: "bg-sky-500", opened: "bg-emerald-500", clicked: "bg-amber-500", replied: "bg-pink-500" };

function pct(n, d) {
  if (!d) return "—";
  return `${((n / d) * 100).toFixed(1)}%`;
}

export function OverviewTab({ wsId }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    setData(null);
    api.get(`/workspaces/${wsId}/marketing/overview?days=${days}`).then((r) => setData(r.data));
  }, [wsId, days]);

  if (!data) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-foreground/60">Loading analytics…</CardContent></Card>;
  const t = data.totals || {};
  const kpis = [
    { label: "Sent", value: t.sent, sub: null },
    { label: "Delivered", value: t.delivered, sub: pct(t.delivered, t.sent) },
    { label: "Opened", value: t.opened, sub: pct(t.opened, t.sent) },
    { label: "Clicked", value: t.clicked, sub: pct(t.clicked, t.sent) },
    { label: "Replied", value: t.replied, sub: pct(t.replied, t.sent) },
    { label: "Unsubscribed", value: t.unsubscribed, sub: pct(t.unsubscribed, t.sent) },
  ];

  return (
    <div className="space-y-4" data-testid="marketing-overview-tab">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold flex-1">Engagement over the last {days} days</span>
        {RANGES.map((r) => (
          <Button key={r} size="sm" variant={days === r ? "default" : "outline"} className="rounded-none h-7 px-3 text-xs"
            onClick={() => setDays(r)} data-testid={`overview-range-${r}`}>{r}d</Button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="overview-kpis">
        {kpis.map((k) => (
          <Card key={k.label} className="rounded-none">
            <CardContent className="p-3">
              <div className="label-eyebrow text-[10px]">{k.label}</div>
              <div className="text-xl font-bold font-mono" data-testid={`overview-kpi-${k.label.toLowerCase()}`}>{k.value ?? 0}</div>
              {k.sub && <div className="text-[10px] text-foreground/50">{k.sub} of sent</div>}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="rounded-none" data-testid="overview-timeseries">
        <CardContent className="p-4">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  {SERIES_KEYS.map((s) => (
                    <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={s.color} stopOpacity={0.25} />
                      <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {SERIES_KEYS.map((s) => (
                  <Area key={s.key} type="monotone" dataKey={s.key} stroke={s.color} strokeWidth={1.8}
                    fill={`url(#grad-${s.key})`} dot={false} />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <FunnelCard title="Top campaigns" icon={Send} rows={data.top_campaigns} testid="overview-top-campaigns" />
        <FunnelCard title="Top sequences" icon={GitBranch} rows={data.top_sequences} testid="overview-top-sequences" />
      </div>
    </div>
  );
}

function FunnelCard({ title, icon: Icon, rows, testid }) {
  return (
    <Card className="rounded-none" data-testid={testid}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold"><Icon className="w-4 h-4 text-primary" />{title}</div>
        {(!rows || rows.length === 0) ? (
          <div className="text-xs text-foreground/50 py-6 text-center">No activity in this window yet.</div>
        ) : rows.map((r) => (
          <div key={r.id} className="space-y-1" data-testid={`${testid}-row-${r.id}`}>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium truncate flex-1">{r.name}</span>
              <Badge variant="outline" className="rounded-none text-[10px]">{r.sent} sent</Badge>
            </div>
            <div className="space-y-0.5">
              {FUNNEL_STAGES.map((stage) => {
                const max = r.sent || 1;
                const w = Math.max(r[stage] ? 4 : 0, Math.round((r[stage] / max) * 100));
                return (
                  <div key={stage} className="flex items-center gap-2">
                    <span className="w-16 text-[10px] text-foreground/55 capitalize">{stage}</span>
                    <div className="flex-1 h-3 bg-foreground/5">
                      <div className={`h-3 ${STAGE_COLORS[stage]}`} style={{ width: `${Math.min(w, 100)}%` }} />
                    </div>
                    <span className="w-14 text-[10px] font-mono text-right">{r[stage]} <span className="text-foreground/40">{pct(r[stage], r.sent)}</span></span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
